import React, { useEffect, useState } from "react";
import { API, adminHeaders, adminWrite } from "@/lib/api";

const STATUT_LABEL = { recu: "Reçu", structure: "Structuré", erreur: "Erreur" };

function useNoindex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
}

function Field({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "5px 0", borderBottom: "1px solid var(--li2)", fontSize: 13 }}>
      <span className="t-label" style={{ minWidth: 190 }}>{label}</span>
      <span style={{ fontFamily: "Archivo" }}>{value}</span>
    </div>
  );
}

function StructureView({ s }) {
  if (!s) return <p className="t-body" style={{ fontSize: 13, color: "var(--ink3)" }}>Extraction en attente…</p>;
  const c = s.cible || {};
  return (
    <div data-testid="capture-structure">
      <Field label="secteur" value={s.secteur || "—"} />
      <Field label="objectif" value={s.objectif || "—"} />
      <Field label="cible âge" value={`${c.age_min ?? 0} – ${c.age_max ?? 0}`} />
      <Field label="cible geo" value={`${c.geo || "—"}${c.ville ? " · " + c.ville : ""}`} />
      <Field label="cible intérêts" value={(c.interets || []).join(", ") || "—"} />
      <Field label="budget total estimé" value={s.budget_total_estime ? `${s.budget_total_estime} €` : "—"} />
      <Field label="dates" value={`${s.date_debut || "—"} → ${s.date_fin || "—"} (${s.duree_semaines || 0} sem.)`} />
      <Field label="personne réelle à l'image" value={s.personne_reelle_a_l_image ? "oui" : "non"} />
      <Field label="produit physique manipulé" value={s.produit_physique_manipule ? "oui" : "non"} />
      <Field label="lieu réel à montrer" value={s.lieu_reel_a_montrer ? "oui" : "non"} />
      <Field label="univers irréel" value={s.univers_irreel ? "oui" : "non"} />
      <Field label="nb variantes souhaitées" value={s.nb_variantes_souhaitees ?? 0} />
      <Field label="délai (jours)" value={s.delai_jours ?? 0} />
      <Field label="nb formats souhaités" value={s.nb_formats_souhaites ?? 0} />
      <Field label="mots exploitables" value={s.mots_exploitables ?? 0} />
      <Field label="secteur sensible" value={s.secteur_sensible ? "oui" : "non"} />
      <Field label="confiance extraction" value={`${s.confiance_extraction ?? 0} / 100`} />
    </div>
  );
}

export default function CapturesAdmin() {
  useNoindex();
  const [token, setToken] = useState(localStorage.getItem("sliz_admin_token") || "");
  const [email, setEmail] = useState("admin@sliz.fr");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [captures, setCaptures] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polVersion, setPolVersion] = useState("");
  const [polSaved, setPolSaved] = useState(false);
  const [actionErr, setActionErr] = useState("");

  const load = async () => {
    if (!token) return;
    setLoading(true);
    const res = await fetch(`${API}/admin/captures`, { headers: adminHeaders() });
    if (res.status === 401) { localStorage.removeItem("sliz_admin_token"); setToken(""); setLoading(false); return; }
    const list = res.ok ? await res.json() : [];
    setCaptures(list);
    setLoading(false);
    try {
      const c = await fetch(`${API}/content`).then((r) => r.json());
      setPolVersion(c?.settings?.politique_version || "");
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const savePolVersion = async () => {
    setPolSaved(false);
    try {
      await adminWrite("/admin/settings/politique_version", { method: "PUT", body: { value: polVersion.trim() } });
      setActionErr("");
      setPolSaved(true);
      setTimeout(() => setPolSaved(false), 2500);
    } catch (e) {
      setActionErr("Enregistrement REFUSÉ — " + e.message + " Rien n'a été écrit ; votre saisie est conservée.");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoginErr("");
    const res = await fetch(`${API}/auth/login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setLoginErr(typeof d.detail === "string" ? d.detail : "Connexion refusée");
      return;
    }
    const d = await res.json();
    localStorage.setItem("sliz_admin_token", d.token);
    setToken(d.token);
  };

  const exportCsv = async () => {
    const res = await fetch(`${API}/admin/captures/export.csv`, { headers: adminHeaders() });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "brief_captures.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const reextract = async (id) => {
    try {
      await adminWrite(`/admin/captures/${id}/reextract`, { method: "POST" });
      setActionErr("");
      setTimeout(load, 1500);
    } catch (e) {
      setActionErr("Ré-extraction REFUSÉE — " + e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce brief capturé ?")) return;
    try {
      await adminWrite(`/admin/captures/${id}/delete`, { method: "POST" });
      setActionErr("");
      if (sel === id) setSel(null);
      load();
    } catch (e) {
      setActionErr("Suppression REFUSÉE — " + e.message);
    }
  };

  if (!token) {
    return (
      <main style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--pp2)" }} data-testid="captures-login">
        <form onSubmit={login} style={{ width: 360, background: "#fff", border: "1px solid var(--li)", padding: 40, display: "grid", gap: 22 }}>
          <span className="t-logo" style={{ fontSize: 20 }}>SLI<span style={{ color: "var(--roi)" }}>Z</span></span>
          <p className="t-label" style={{ margin: 0 }}>Captures de briefs</p>
          <div className="field">
            <label htmlFor="c-email">Email</label>
            <input id="c-email" data-testid="captures-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="c-pass">Mot de passe</label>
            <input id="c-pass" data-testid="captures-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {loginErr && <p style={{ color: "#8C1D18", fontSize: 13, margin: 0 }} data-testid="captures-login-error">{loginErr}</p>}
          <button className="btn btn-primary" type="submit" data-testid="captures-login-submit">Connexion</button>
        </form>
      </main>
    );
  }

  return (
    <main style={{ minHeight: "100vh", background: "var(--pp2)" }} data-testid="captures-panel">
      <div style={{ background: "#fff", borderBottom: "1px solid var(--li)" }}>
        <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <span className="t-logo" style={{ fontSize: 16 }}>SLI<span style={{ color: "var(--roi)" }}>Z</span> — Captures</span>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <a href="/admin" className="t-label" data-testid="captures-back-admin">Back-office</a>
            <button className="t-label" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="captures-logout"
              onClick={() => { localStorage.removeItem("sliz_admin_token"); setToken(""); }}>Déconnexion</button>
          </div>
        </div>
      </div>

      {actionErr && (
        <div className="wrap" style={{ paddingTop: 18 }}>
          <p style={{ margin: 0, padding: "12px 16px", background: "#FDECEA", border: "1px solid #8C1D18", color: "#8C1D18", fontSize: 13 }} data-testid="captures-action-error">{actionErr}</p>
        </div>
      )}

      <div className="wrap" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <p className="t-label" style={{ margin: 0 }} data-testid="captures-count">{captures.length} brief(s) capturé(s){loading ? " · chargement…" : ""}</p>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="btn" style={{ padding: "8px 14px" }} onClick={load} data-testid="captures-refresh">Actualiser</button>
            <button className="btn btn-primary" style={{ padding: "8px 14px" }} onClick={exportCsv} data-testid="captures-export">Export CSV</button>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 32, flexWrap: "wrap" }} data-testid="captures-policy">
          <div className="field" style={{ maxWidth: 260 }}>
            <label htmlFor="pol-version">Version de la politique de confidentialité en vigueur</label>
            <input id="pol-version" data-testid="captures-policy-input" value={polVersion} onChange={(e) => setPolVersion(e.target.value)} placeholder="2026-09-08" />
          </div>
          <button className="btn" style={{ padding: "10px 16px" }} onClick={savePolVersion} data-testid="captures-policy-save">Enregistrer</button>
          {polSaved && <span className="t-label" style={{ color: "var(--roi)" }} data-testid="captures-policy-saved">Enregistré</span>}
        </div>

        {captures.length === 0 && <p className="t-body">Aucun brief capturé pour le moment.</p>}

        {captures.map((c) => {
          const conf = (c.brief_structure || {}).confiance_extraction;
          const open = sel === c.id;
          return (
            <div key={c.id} style={{ borderTop: "1px solid var(--li)", padding: "18px 0" }} data-testid={`capture-${c.id}`}>
              <button onClick={() => setSel(open ? null : c.id)} style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: 0 }} data-testid={`capture-row-${c.id}`}>
                <div style={{ display: "flex", gap: 16, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span className="t-label" style={{ minWidth: 150 }}>{c.cree_le ? new Date(c.cree_le).toLocaleString("fr-FR") : "—"}</span>
                  <span style={{ fontVariationSettings: '"wght" 700', minWidth: 160 }}>{c.societe || "—"}</span>
                  <span style={{ fontSize: 14 }}>{c.email || "—"}</span>
                  <span className="t-label" style={{ color: c.statut === "erreur" ? "#8C1D18" : c.statut === "structure" ? "var(--roi)" : "var(--ink2)" }}>{STATUT_LABEL[c.statut] || c.statut}</span>
                  <span className="t-label">confiance {conf ?? "—"}{conf != null ? "/100" : ""}</span>
                </div>
              </button>

              {open && (
                <div className="grid gap-8 lg:grid-cols-2" style={{ marginTop: 18 }} data-testid={`capture-detail-${c.id}`}>
                  <div>
                    <p className="t-label" style={{ marginBottom: 10 }}>Brief brut</p>
                    <p className="t-body" style={{ fontSize: 14, whiteSpace: "pre-wrap", background: "#fff", border: "1px solid var(--li)", padding: 16, margin: 0, minHeight: 120 }} data-testid={`capture-brut-${c.id}`}>{c.brief_brut || "—"}</p>
                    <p className="t-label" style={{ marginTop: 14 }}>tél. {c.telephone || "—"} · IP {c.ip || "—"} · politique {c.politique_version || "—"}</p>
                    {c.statut === "erreur" && (
                      <div style={{ marginTop: 14 }}>
                        <p className="t-body" style={{ color: "#8C1D18", fontSize: 13, margin: "0 0 10px" }}>Erreur : {c.erreur_message || "inconnue"}</p>
                        <button className="btn" style={{ padding: "8px 14px" }} onClick={() => reextract(c.id)} data-testid={`capture-reextract-${c.id}`}>Relancer l'extraction</button>
                      </div>
                    )}
                    <div style={{ marginTop: 14 }}>
                      <button className="btn" style={{ padding: "8px 14px", color: "#8C1D18" }} onClick={() => remove(c.id)} data-testid={`capture-delete-${c.id}`}>Supprimer ce brief</button>
                    </div>
                  </div>
                  <div>
                    <p className="t-label" style={{ marginBottom: 10 }}>Brief structuré</p>
                    <div style={{ background: "#fff", border: "1px solid var(--li)", padding: 16 }}>
                      <StructureView s={c.brief_structure} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </main>
  );
}
